<!-- 
  Componente FormDinamico
  
  Este componente genera un formulario dinámico a partir de un array de campos (`fields`) que se pasa como prop. 
  Cada campo del formulario puede tener validaciones, valores por defecto y comportamientos condicionales.
  
  Uso:
  <FormDinamico
    :fields="formFields"
    storeGetterPath="ruta/al/getter"
    storeGetterName="nombreDelGetter"
    storeSetterPath="ruta/al/setter"
    storeSetterName="nombreDelSetter"
  />
  
  Props:
  - fields: Array de objetos que definen los campos del formulario. Cada campo puede incluir:
    - name: Nombre único del campo.
    - label: Etiqueta que se muestra en el formulario.
    - type: Tipo de campo (ej. input, dropdown, textarea).
    - value: Valor inicial o por defecto del campo.
    - validation: Reglas de validación (opcional) como required, min, max, isNumeric, etc.
    - conditional: Lógica condicional para mostrar u ocultar otros campos basados en el valor de este campo (opcional).
  
  - storeGetterPath: Ruta de Vuex donde se encuentra el getter para cargar los datos.
  - storeGetterName: Nombre del getter para obtener los datos iniciales del formulario desde Vuex.
  - storeSetterPath: Ruta de Vuex donde se encuentra el setter para guardar los datos.
  - storeSetterName: Nombre del setter para enviar los datos del formulario a Vuex.
  
  Funcionalidades principales:
  - Carga de datos desde Vuex para pre-poblar los campos.
  - Validaciones personalizables como required, min, max, isNumeric, isDate.
  - Manejo de campos condicionales que se muestran u ocultan según el valor de otros campos.
  - Envío de los datos actualizados a Vuex usando el setter especificado.
  
  Ejemplo de configuración de campos:
  const formFields = [
    { name: "nombre", label: "Nombre", type: "input", value: "", validation: { required: true, min: 3, max: 50 } },
    { name: "edad", label: "Edad", type: "input", value: "", validation: { required: true, isNumeric: true } },
    { name: "tipoCuenta", label: "Tipo de Cuenta", type: "dropdown", value: "", options: [ { label: "Ahorros", value: "ahorros" }, { label: "Corriente", value: "corriente" } ] }
  ];
 /-->
<template>
  <form @submit.prevent="submitForm">
    <div class="grid">
      <div
        v-for="(field, index) in formFields"
        :key="index"
        :style="{ display: field.hidden ? 'none' : 'block' }"
        :class="`col-${field.cols || 6}`"
      >
        <label :for="field.name"
          >{{ field.label
          }}<span v-if="field.validation?.required" style="color: red"> *</span></label
        >
        <component
          :is="getComponentType(field.type)"
          v-model="formData[field.name]"
          v-bind="field.props"
          :mask="field.mask"
          :options="field.options"
          optionLabel="nombre"
          :placeholder="field.placeholder"
          :class="['form-field col-12 mt-1', { 'input-error': errors[field.name] }]"
          @change="handleFieldChange(field, formData[field.name])"
          @blur="() => handleBlur(field.name)"
          :readonly="isReadonly && ['input', 'inputMask', 'textarea'].includes(field.type)"
          :disabled="isReadonly && ['dropdown'].includes(field.type)"
        />
        <label v-if="errors[field.name]" class="error-text">{{ errors[field.name] }}</label>
      </div>
    </div>
    <div v-if="!isReadonly && showSaveButton" class="d-flex justify-content-end mt-3">
      <Button type="submit" label="Guardar" icon="pi pi-save" />
    </div>
  </form>
</template>

<script>
import Button from "primevue/button";
import InputMask from "primevue/inputmask";

export default {
  name: "FormDinamico",
  components: {
    Button,
    InputMask,
  },
  props: {
    fields: {
      type: Array,
      required: true,
    },
    storeGetterPath: {
      type: String,
      required: true,
    },
    storeGetterName: {
      type: String,
      required: true,
    },
    storeSetterPath: {
      type: String,
      required: true,
    },
    storeSetterName: {
      type: String,
      required: true,
    },
    storeGetterAllName: {
      type: String,
      required: false,
    },
    storeGetterAllName: {
      type: String,
      required: false,
    },
    isReadonly: {
      type: Boolean,
      default: false,
    },
    showSaveButton: {
      type: Boolean,
      default: true,
    },
    externalData: {
      type: Object,
      default: () => ({}),
    },
  },
  data() {
    return {
      arrId: null,
      formFields: [],
      formData: {},
      errors: {},
    };
  },
  created() {
    this.arrId = this.$route.query.id;
    this.formFields = [...this.fields];
    this.initializeFormData();
  },
  mounted() {
    this.loadStoreData();
  },
  methods: {
    async loadStoreData() {
      try {
        const Getter = `${this.storeGetterPath}/${this.storeGetterName}`;
        const data = this.$store.getters[Getter](this.arrId);
        if (data) {
          Object.keys(data.doc).forEach((key) => {
            this.$set(this.formData, key, data.doc[key]);
          });
        }
        this.formFields.forEach((field) => {
          if (!this.formData[field.name] && field.value !== undefined) {
            this.$set(this.formData, field.name, field.value);
          }
        });

        this.formFields.forEach((field) => {
          const value = this.formData[field.name];
          if (field.conditional && typeof field.conditional.condition === "function") {
            this.handleFieldChange(field, value);
          }
        });
      } catch (error) {
        console.error("Error al cargar los datos:", error);
      }
    },

    initializeFormData() {
      this.formFields.forEach((field) => {
        if (this.isReadonly && this.externalData[field.name] !== undefined) {
          this.$set(this.formData, field.name, this.externalData[field.name]);
        } else if (field.value !== undefined) {
          this.$set(this.formData, field.name, field.value);
        } else {
          this.$set(this.formData, field.name, "");
        }
      });
    },

    getComponentType(type) {
      const componentTypes = {
        input: "InputText",
        inputMask: "InputMask",
        dropdown: "Dropdown",
        textarea: "Textarea",
      };

      return componentTypes[type] || "input";
    },

    handleFieldChange(field, value) {
      this.$set(this.formData, field.name, value);
      if (!value) return;
      if (field.conditional && typeof field.conditional.condition === "function") {
        const shouldAddFields = field.conditional.condition(value.value);
        const currentIndex = this.formFields.findIndex((f) => f.name === field.name);
        if (shouldAddFields) {
          field.conditional.fields.forEach((newField) => {
            if (!this.formFields.some((f) => f.name === newField.name)) {
              if (this.isReadonly) newField.disabled = true;
              this.formFields.splice(currentIndex + 1, 0, newField);
              const existingValue = this.formData[newField.name];
              const newValue =
                existingValue !== undefined && existingValue !== null
                  ? existingValue
                  : newField.value || "";
              this.$set(this.formData, newField.name, newValue);
            }
          });
        } else {
          const fieldsToRemove = field.conditional.fields.map((f) => f.name);
          this.formFields = this.formFields.filter((f) => !fieldsToRemove.includes(f.name));
          fieldsToRemove.forEach((name) => {
            this.$delete(this.errors, name);
            this.$delete(this.formData, name);
          });
        }
      }
    },

    validateField(field) {
      this.$delete(this.errors, field.name);
      const value = this.formData[field.name];
      const { validation } = field;

      if (validation) {
        let error = null;
        const validators = [
          this.validateRequired,
          this.validateMinLength,
          this.validateMaxLength,
          this.validateNumeric,
          this.validateDate,
          this.validateUnique,
          this.validateDate,
        ];

        for (const validator of validators) {
          error = validator(value, validation);
          if (error) break;
        }

        if (error) {
          this.$set(this.errors, field.name, error);
        } else {
          this.$delete(this.errors, field.name);
        }
      }
    },

    validateRequired(value, validation) {
      if (validation.required && !value) {
        return "Este campo es requerido.";
      }
      return null;
    },

    validateMinLength(value, validation) {
      if (value && validation.min && value.length < validation.min) {
        return `Tienes que ingresar al menos ${validation.min} caracteres.`;
      }
      return null;
    },

    validateMaxLength(value, validation) {
      if (value && validation.max && value.length > validation.max) {
        return `No puedes ingresar más de ${validation.max} caracteres en este campo.`;
      }
      return null;
    },

    validateNumeric(value, validation) {
      if (value && validation.isNumeric && isNaN(value)) {
        return "Este campo debe ser un número válido.";
      }
      return null;
    },

    validateDate(value, validation) {
      if (value && validation.isDate) {
        const datePattern = /^(0?[1-9]|[12][0-9]|3[01])\/(0?[1-9]|1[0-2])\/\d{4}$/;
        if (!datePattern.test(value)) {
          return "Debe ingresar una fecha válida en formato dd/mm/aaaa.";
        } else {
          const [day, month, year] = value.split("/").map((part) => parseInt(part, 10));
          const inputDate = new Date(year, month - 1, day);
          const today = new Date();
          today.setHours(0, 0, 0, 0);
          if (inputDate > today) {
            return "No puedes ingresar una fecha futura.";
          }
        }
      }
      return null;
    },
    validateUnique(field, validation) {
      if (validation.unique) {
        const Getter = `${this.storeGetterPath}/${this.storeGetterAllName}`;
        const data = this.$store.getters[Getter];
        const exists = data.some(
          (afectacion) =>
            afectacion.type === "doc" &&
            afectacion.bienEntregado?.value === field.value &&
            afectacion.arrId !== this.arrId
        );
        if (exists) {
          return `Este bien ya pertenece a una afectación.`;
        }
      }
      return null;
    },

    updateFields() {
      this.formFields = this.fields;
      this.formData = this.formFields;
      this.formFields.forEach((field) => {
        if (field.value !== undefined) {
          this.$set(this.formData, field.name, field.value);
        }
        const value = this.formData[field.name];
        if (field.conditional && typeof field.conditional.condition === "function") {
          this.handleFieldChange(field, value);
        }
      });

      this.$forceUpdate();
    },

    handleUpdateData() {
      const dynamicKeys = this.fields
        .filter((field) => field.type === "dropdown")
        .map((field) => field.name);
      const data = this.$store.getters[`${this.storeGetterPath}/${this.storeGetterName}`](
        this.arrId
      );
      const storeSetter = `${this.storeSetterPath}/${this.storeSetterName}`;
      const cleanedData = Object.fromEntries(
        Object.entries(this.formData).filter(([key, value]) => {
          return (
            dynamicKeys.includes(key) ||
            (!(typeof value === "object" && value !== null && value.__ob__) &&
              typeof value !== "function")
          );
        })
      );
      if (data) {
        this.$store.dispatch(storeSetter, {
          ...data,
          doc: cleanedData,
        });
      }
      this.$emit("submit");
    },

    handleBlur(fieldName) {},
    submitForm() {
      this.formFields.forEach(this.validateField);
      if (Object.keys(this.errors).length === 0) {
        this.handleUpdateData();
      }
    },
  },
  watch: {
    fields: {
      handler(newFields) {
        this.updateFields();
      },
      deep: true,
    },
    arrId: {
      handler() {
        this.loadStoreData();
      },
      immediate: true,
    },
    externalData: {
      immediate: true,
      deep: true,
      handler(newData) {
        if (this.isReadonly && newData) {
          for (const key of Object.keys(newData)) {
            this.$set(this.formData, key, newData[key]);
          }
        }
      },
    },
  },
};
</script>

<style scoped>
.error-text {
  color: red;
  font-size: 0.85rem;
  margin-top: 0.5rem;
}

.input-error {
  border-color: red !important;
}

.d-flex {
  display: flex;
}

.justify-content-end {
  justify-content: flex-end;
}

.form-field:not(textarea) {
  height: 48px;
  padding: 0.375rem 0.75rem;
  font-size: 1rem;
  line-height: 1.5;
  border-radius: 0.25rem;
  border: 1px solid #ced4da;
  box-sizing: border-box;
}
</style>
